import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { FaShoppingBasket } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useConfetti } from "../../../context/ConfettiContext";
import PlayToolbarScrollRow from "../../../pages/components/PlayToolbarScrollRow";
import SocialShareDropdown, {
    buildShareUrl,
} from "../../../pages/puzzles/common/components/SocialShareDropdown";
import PlayElapsedTimer from "../../../pages/puzzles/common/components/PlayElapsedTimer";
import { resolveJigsawShareTemplate } from "../../../pages/puzzles/jigsaw/jigsawBrowseUtils";
import BoardMatColorSwitcher from "../../../pages/puzzles/jigsaw/BoardMatColorSwitcher";
import CanvasBoard from "../../../pages/puzzles/jigsaw/CanvasBoard";
import JigsawColorBasketOverlay from "../../../pages/puzzles/jigsaw/JigsawColorBasketOverlay";
import JigsawColorBasketPlayService from "../../../pages/puzzles/jigsaw/JigsawColorBasketPlayService";
import AdminColorBasketPlayPanel from "../../../admin/jigsaw/play/AdminColorBasketPlayPanel";
import { usePagesTranslation } from "../../../pages/locales";
import PieceTrayScrollRows from "../../../pages/puzzles/jigsaw/PieceTrayScrollRows";
import PieceTrayScrollColumns from "../components/PieceTrayScrollColumns";
import {
    didDroppedPieceLandAtHome,
    findJigsawLineMatchCelebration,
    isPieceExactlyAtHome,
} from "../../../pages/puzzles/jigsaw/jigsawPieceGroups";
import {
    ensurePieceSettleStyle,
    PIECE_SETTLE_TOTAL_MS,
} from "../../../pages/puzzles/jigsaw/jigsawPieceSettleEffects";
import { PIECE_SHINE_CLEANUP_MS } from "../../../pages/puzzles/jigsaw/PieceLockShineOverlay";
import { LINE_COMPLETE_BANNER_MS } from "../../../pages/puzzles/sliding/SlidingLineCompleteFx";
import {
    BOARD_SIZE,
    PieceVisual,
    pieceVisualCellOffsetPx,
    pieceVisualRenderSizePx,
} from "../../../pages/puzzles/jigsaw/PieceVisual";
import { useBoardMatColor } from "../../../pages/puzzles/jigsaw/useBoardMatColor";
import SlidingPlayMistBackground from "../../../pages/puzzles/sliding/SlidingPlayMistBackground";
import { SITE_BG, SURFACE, TEXT } from "../../../dark-theme/palette";
import { panelSurfaceSx } from "../../../dark-theme/surfaces";
import ApiService from "../../../services/ApiService";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import {
    animatePieceAlongCurve,
    boardHomeScreenRect,
    flyControlPoint,
} from "../../mobile-jigsaw-animation/lib/pieceFlyAnimation";
import { getImageUrl, getStoryTitle, getTemplateGrid } from "../lib/api";
import { normalizeHomeJigsawGroup } from "../lib/jigsawGroup";
import {
    getJigsawRecordingCaptureFrame,
    RECORDING_FRAME_GOOGLE_1080P,
} from "../recordingCaptureFrame";
import {
    computeBoardSidePx,
    computePreviewSidePx,
    isTrayBesideBoard,
    PAD,
    PREVIEW_GAP,
    TOOLBAR_GAP,
    TOOLBAR_H,
    TOOLBAR_ICON_SX,
    TRAY_CELL_GAP,
    TRAY_COLUMN_COUNT,
    TRAY_PIECE_SCALE,
    TRAY_ROW_COUNT,
    TRAY_GAP,
    trayBlockPx,
} from "../recordingCaptureLayout";
import { MAIN_PATH } from "../routes";

/** Match play-jigsaw night chrome: light icons on frosted dark toolbar. */
const PLAY_TOOLBAR_ICON_SX = {
    ...TOOLBAR_ICON_SX,
    color: TEXT.primary,
};
/** Raised tray plate over the night mist (same as JigsawPlayBoardPage). */
const TRAY_PLATE_BG = "rgba(15, 23, 42, 0.88)";

/** Board zoom steps; the board scrolls inside its slot above 1 so the frame keeps its layout. */
const BOARD_ZOOM_LEVELS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const BOARD_ZOOM_MIN = BOARD_ZOOM_LEVELS[0];
const BOARD_ZOOM_MAX = BOARD_ZOOM_LEVELS[BOARD_ZOOM_LEVELS.length - 1];

/** Placement hint — same sequence as JigsawPlayBoardPage (no credit spend on capture page). */
const PLACEMENT_HINT_COUNT = 3;
const PLACEMENT_HINT_PICK_MS = 1000;
const PLACEMENT_HINT_STEP_MS = 1200;
const PLACEMENT_HINT_HOLD_MS = 10_000;
/** Let the completed board show before confetti (no success popup on capture). */
const SOLVE_SUCCESS_DELAY_MS = 2000;

/** Hands-free capture: hold, then fly every tray piece home one by one. */
const AUTO_PLACE_INTRO_MS = 1800;
/** Target wall-clock for the whole auto-place run (9×9 Medium ≈ 2–2½ min). */
const AUTO_PLACE_BUDGET_MS = 150_000;
const AUTO_PLACE_FLY_MIN_MS = 420;
const AUTO_PLACE_FLY_MAX_MS = 1100;
const AUTO_PLACE_GAP_MS = 220;
/** Hold after a row/column completes so the banner/SFX can finish before the next fly. */
const AUTO_PLACE_LINE_HOLD_MS = LINE_COMPLETE_BANNER_MS + 200;

function paceFlyMs(count, budgetMs, minMs, maxMs, gapMs) {
    if (count <= 0) return maxMs;
    const perPiece = budgetMs / count - gapMs;
    return Math.round(Math.min(maxMs, Math.max(minMs, perPiece)));
}

function hashSeed(key) {
    let h = 2166136261;
    const s = String(key ?? "");
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function mulberry32(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function edgeDist(pieceIndex, grid) {
    const row = Math.floor(pieceIndex / grid);
    const col = pieceIndex % grid;
    return Math.min(row, col, grid - 1 - row, grid - 1 - col);
}

function chebyshev(a, b, grid) {
    const ar = Math.floor(a / grid);
    const ac = a % grid;
    const br = Math.floor(b / grid);
    const bc = b % grid;
    return Math.max(Math.abs(ar - br), Math.abs(ac - bc));
}

/**
 * Human-like place order: edge pieces first as a soft bias, work in local clusters,
 * occasionally jump to another region — not a perfect spiral/computer sweep.
 */
function humanStylePlaceOrder(grid, seedKey = "") {
    const n = Math.max(0, Number(grid) || 0);
    if (n <= 0) return [];
    const rand = mulberry32(hashSeed(`human-place:${seedKey}`));
    const remaining = new Set(Array.from({ length: n * n }, (_, i) => i));
    const order = [];
    const corners = [0, n - 1, (n - 1) * n, n * n - 1];
    let start;
    if (rand() < 0.72) {
        start = corners[Math.floor(rand() * corners.length)];
    } else {
        const edges = [...remaining].filter((i) => edgeDist(i, n) === 0);
        start = edges[Math.floor(rand() * edges.length)] ?? 0;
    }
    order.push(start);
    remaining.delete(start);
    let last = start;

    while (remaining.size > 0) {
        const candidates = [...remaining];
        const progress = order.length / (n * n);
        let best = candidates[0];
        let bestScore = Infinity;
        for (const idx of candidates) {
            const neigh = chebyshev(idx, last, n);
            const edge = edgeDist(idx, n);
            const edgeBias = edge * (2.5 - progress * 1.3);
            const nearBias = neigh <= 1 ? 0 : neigh <= 2 ? 0.65 : neigh * 1.4;
            const noise = rand() * (rand() < 0.14 ? 4.2 : 1.05);
            const score = edgeBias + nearBias + noise;
            if (score < bestScore) {
                bestScore = score;
                best = idx;
            }
        }
        if (rand() < 0.09 && remaining.size > 8) {
            const edges = candidates.filter((i) => edgeDist(i, n) === 0);
            if (edges.length > 0) {
                best = edges[Math.floor(rand() * edges.length)];
            }
        }
        order.push(best);
        remaining.delete(best);
        last = best;
    }
    return order;
}

/** Same tray→board arc as default, with per-move jitter so flights don’t look robotic. */
function humanFlyControl(fromRect, toRect, rand) {
    const base = flyControlPoint(fromRect, toRect);
    const span = Math.hypot(toRect.left - fromRect.left, toRect.top - fromRect.top);
    const jitter = Math.max(18, span * 0.14);
    return {
        x: base.x + (rand() - 0.5) * jitter * 2.2,
        y: base.y + (rand() - 0.4) * jitter * 1.4,
    };
}

const trayHintPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(59, 166, 102, 0.55), 0 0 8px rgba(59, 166, 102, 0.35);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(59, 166, 102, 0.95), 0 0 16px rgba(59, 166, 102, 0.75);
  }
`;

function pickFirstTrayPieces(trayPiecesInOrder, count = PLACEMENT_HINT_COUNT) {
    const arr = Array.isArray(trayPiecesInOrder) ? trayPiecesInOrder : [];
    return arr.slice(0, Math.min(count, arr.length));
}

function newBoardPieceId() {
    return `bp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

/** Next zoom step (direction 1 = in, -1 = out); returns current at the limits. */
function stepBoardZoom(current, direction) {
    const eps = 0.001;
    if (direction > 0) {
        return BOARD_ZOOM_LEVELS.find((level) => level > current + eps) ?? current;
    }
    const lower = BOARD_ZOOM_LEVELS.filter((level) => level < current - eps);
    return lower.length > 0 ? lower[lower.length - 1] : current;
}

/**
 * Prototype-only fixed-frame jigsaw capture board (Google 1920×1080 or Apple 1290×2796).
 * Not the service play page — fixed frame, OBS-safe explicit board scale.
 */
export default function Recording1080pBoardPage() {
    const { storyId, jigsawId } = useParams();
    const { pathname } = useLocation();
    const frame =
        getJigsawRecordingCaptureFrame(pathname) ?? RECORDING_FRAME_GOOGLE_1080P;
    const FRAME_W = frame.width;
    const FRAME_H = frame.height;
    /**
     * Dialogs portal to the body and center on the browser viewport, which is not the capture
     * frame unless the window happens to be frame-sized. Pin them to the frame box instead.
     */
    const captureDialogSx = useMemo(
        () => ({ position: "absolute", top: 0, left: 0, width: FRAME_W, height: FRAME_H }),
        [FRAME_W, FRAME_H],
    );
    const navigate = useNavigate();
    const { t } = usePagesTranslation();
    const { triggerConfetti } = useConfetti();
    const boardRef = useRef(null);
    const boardViewportRef = useRef(null);
    const boardPanRef = useRef(null);
    const draggingRef = useRef(null);
    const trayRef = useRef(null);
    const flyNodeRef = useRef(null);
    const flyInnerRef = useRef(null);
    const autoPlacePendingRef = useRef([]);
    const pausedRef = useRef(false);
    const { mat: boardMat, matId: boardMatId, setMatId: setBoardMatId, grooveStyle: boardGrooveStyle } =
        useBoardMatColor();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [template, setTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [story, setStory] = useState(null);
    const [boardPieces, setBoardPieces] = useState([]);
    const boardPiecesRef = useRef([]);
    const [trayOrder, setTrayOrder] = useState([]);
    const [dragging, setDragging] = useState(null);
    /** Auto-place flight: { pieceIndex, frame } */
    const [flying, setFlying] = useState(null);
    /** Correct-placement shine overlays: { boardId, pieceIndex, id }[] */
    const [activePieceShines, setActivePieceShines] = useState([]);
    /** boardId → key for the wrong-place pop & settle animation */
    const [pieceSettleKeys, setPieceSettleKeys] = useState({});
    /** Row/column complete praise banner: { id, rows, cols, praise } */
    const [lineMatchFx, setLineMatchFx] = useState(null);
    const lineMatchFxIdRef = useRef(0);
    /** Placement hint: { pieceIndices, mode: "pick"|"step"|"all", stepIndex } */
    const [placementHint, setPlacementHint] = useState(null);
    const [placementHintBusy, setPlacementHintBusy] = useState(false);
    const placementHintRunIdRef = useRef(0);
    const traySlotRefs = useRef(new Map());
    const fxTimersRef = useRef([]);
    const [paused, setPaused] = useState(false);
    const [boardZoom, setBoardZoom] = useState(1);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [colorBasketOverlayOpen, setColorBasketOverlayOpen] = useState(false);
    const [colorBasketsAvailable, setColorBasketsAvailable] = useState(false);
    /** In-frame basket practice (keeps capture size; does not leave to public play). */
    const [colorBasketPlay, setColorBasketPlay] = useState(null);
    const successShownRef = useRef(false);
    const successDelayTimerRef = useRef(null);
    const playStartMsRef = useRef(null);
    const pauseStartedAtRef = useRef(null);
    const pausedAccumMsRef = useRef(0);
    const moveCountRef = useRef(0);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById("root");
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            htmlW: html.style.width,
            htmlH: html.style.height,
            bodyW: body.style.width,
            bodyH: body.style.height,
            bodyMargin: body.style.margin,
            rootW: root?.style.width,
            rootH: root?.style.height,
            rootOverflow: root?.style.overflow,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        html.style.width = `${FRAME_W}px`;
        html.style.height = `${FRAME_H}px`;
        body.style.width = `${FRAME_W}px`;
        body.style.height = `${FRAME_H}px`;
        body.style.margin = "0";
        if (root) {
            root.style.width = `${FRAME_W}px`;
            root.style.height = `${FRAME_H}px`;
            root.style.overflow = "hidden";
        }
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            html.style.width = prev.htmlW;
            html.style.height = prev.htmlH;
            body.style.width = prev.bodyW;
            body.style.height = prev.bodyH;
            body.style.margin = prev.bodyMargin;
            if (root) {
                root.style.width = prev.rootW || "";
                root.style.height = prev.rootH || "";
                root.style.overflow = prev.rootOverflow || "";
            }
        };
    }, [FRAME_W, FRAME_H]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                if (!storyId || !jigsawId) {
                    throw new Error("Missing story or jigsaw id");
                }
                const raw = await ApiService.getData(
                    `/home/jigsaw/${encodeURIComponent(storyId)}`,
                );
                const group = normalizeHomeJigsawGroup(raw) ?? raw;
                const templates = Array.isArray(group?.templates) ? group.templates : [];
                const found =
                    templates.find((t) => t?.id === jigsawId) ||
                    templates.find((t) => String(t?.id) === String(jigsawId));
                if (!found) {
                    throw new Error("Jigsaw template not found");
                }
                if (cancelled) return;
                setStory(group?.story ?? null);
                setTemplates(templates);
                setTemplate(found);
                const n = getTemplateGrid(found) || found.tileSize || 0;
                setTrayOrder(humanStylePlaceOrder(n, found.id || jigsawId));
                setBoardPieces([]);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load jigsaw");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [storyId, jigsawId]);

    const grid = getTemplateGrid(template) || template?.tileSize || 0;
    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const total = grid * grid;
    const imageUrl = getImageUrl(story);
    const puzzleTitle = getStoryTitle(story) || template?.title || "Jigsaw";
    const paths = template?.piecePaths?.pieces;
    const svgPathFallback = template?.svgPath;
    const jigsawShareTemplate = useMemo(
        () => resolveJigsawShareTemplate(templates) ?? template,
        [templates, template],
    );
    const jigsawShareId = (() => {
        const raw = jigsawShareTemplate?.id ?? jigsawId;
        const s = raw != null ? String(raw).trim() : "";
        return s || null;
    })();
    const jigsawShareUrl = jigsawShareId ? buildShareUrl(jigsawShareId, "JIGSAW") : "";
    const boardSide = useMemo(
        () => computeBoardSidePx(grid, FRAME_W, FRAME_H),
        [grid, FRAME_W, FRAME_H],
    );
    /** Zoom grows the rendered board only; the slot and the tray keep the frame layout. */
    const boardDisplaySide = boardSide > 0 ? Math.round(boardSide * boardZoom) : 0;
    const boardScale = boardDisplaySide > 0 ? boardDisplaySide / BOARD_SIZE : 1;
    const trayScale = (boardSide > 0 ? boardSide / BOARD_SIZE : 1) * TRAY_PIECE_SCALE;
    const canZoomIn = boardZoom < BOARD_ZOOM_MAX - 0.001;
    const canZoomOut = boardZoom > BOARD_ZOOM_MIN + 0.001;
    const handleBoardZoomStep = useCallback((direction) => {
        setBoardZoom((prev) => stepBoardZoom(prev, direction));
    }, []);

    /** Keep the board centered in its slot after each zoom step. */
    useEffect(() => {
        const el = boardViewportRef.current;
        if (!el) return;
        el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
        el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
    }, [boardZoom, boardDisplaySide]);

    /** Drag-to-pan the zoomed board; a piece grab (sets dragging) wins over panning. */
    const handleBoardPanPointerDown = useCallback(
        (e) => {
            const el = boardViewportRef.current;
            if (!el || boardZoom <= 1 || e.button !== 0) return;
            boardPanRef.current = {
                pointerId: e.pointerId,
                startX: e.clientX,
                startY: e.clientY,
                startScrollLeft: el.scrollLeft,
                startScrollTop: el.scrollTop,
                active: false,
            };
        },
        [boardZoom],
    );

    const handleBoardPanPointerMove = useCallback((e) => {
        const el = boardViewportRef.current;
        const pan = boardPanRef.current;
        if (!el || !pan || pan.pointerId !== e.pointerId) return;
        // A piece was grabbed after press — let the piece drag take over.
        if (draggingRef.current) {
            boardPanRef.current = null;
            el.style.cursor = "";
            return;
        }
        const dx = e.clientX - pan.startX;
        const dy = e.clientY - pan.startY;
        if (!pan.active) {
            if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
            pan.active = true;
            el.style.cursor = "grabbing";
            try {
                el.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
        if (e.cancelable) e.preventDefault();
        el.scrollLeft = pan.startScrollLeft - dx;
        el.scrollTop = pan.startScrollTop - dy;
    }, []);

    const handleBoardPanPointerUp = useCallback((e) => {
        const el = boardViewportRef.current;
        const pan = boardPanRef.current;
        if (!pan || pan.pointerId !== e.pointerId) return;
        if (el) {
            el.style.cursor = "";
            try {
                el.releasePointerCapture(e.pointerId);
            } catch {
                /* already released */
            }
        }
        boardPanRef.current = null;
    }, []);
    const trayPiecePx = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
    const trayCirclePx = trayPiecePx + 2;
    const trayBeside = isTrayBesideBoard(FRAME_W, FRAME_H);
    const trayThicknessPx = trayBlockPx(trayCirclePx, trayBeside);
    const previewSide = trayBeside
        ? computePreviewSidePx(boardSide, trayThicknessPx, FRAME_W)
        : 0;
    const TrayScroller = trayBeside ? PieceTrayScrollColumns : PieceTrayScrollRows;

    useEffect(() => {
        let cancelled = false;
        const id = template?.id;
        if (!id || grid < 9) {
            setColorBasketsAvailable(false);
            return undefined;
        }
        (async () => {
            try {
                const meta = await JigsawColorBasketPlayService.getColorBaskets(id);
                if (cancelled) return;
                setColorBasketsAvailable(meta?.available !== false);
            } catch {
                if (!cancelled) setColorBasketsAvailable(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [template?.id, grid]);

    const handleColorBasketOverlayClose = useCallback(() => {
        setColorBasketOverlayOpen(false);
    }, []);

    const handleBasketChosen = useCallback((idx, payload) => {
        const list = Array.isArray(payload?.baskets) ? payload.baskets : [];
        const basket =
            payload?.basket ||
            list.find((b) => Number(b?.index) === Number(idx)) ||
            null;
        if (!basket) return;
        setColorBasketPlay({
            basket,
            baskets: list,
            basketCount: payload?.basketCount ?? list.length,
            index: Number(idx),
        });
    }, []);

    const handleBackFromBasket = useCallback(() => {
        setColorBasketPlay(null);
    }, []);

    /**
     * Basket practice can be left before the basket is finished, so a basket piece already
     * resting at home belongs on this board at its home cell too — otherwise that work would
     * look lost until the basket is deployed.
     */
    const handleBasketHomePieces = useCallback(
        (pieceIndexes) => {
            if (grid <= 0 || cellPx <= 0 || !Array.isArray(pieceIndexes)) return;
            setBoardPieces((prev) => {
                const onBoard = new Set(prev.map((b) => b.pieceIndex));
                const toAdd = pieceIndexes
                    .filter((idx) => Number.isInteger(idx) && !onBoard.has(idx))
                    .map((pieceIndex) => ({
                        id: newBoardPieceId(),
                        pieceIndex,
                        x: (pieceIndex % grid) * cellPx,
                        y: Math.floor(pieceIndex / grid) * cellPx,
                    }));
                return toAdd.length === 0 ? prev : [...prev, ...toAdd];
            });
        },
        [cellPx, grid],
    );

    const trayPieces = useMemo(
        () => trayOrder.filter((idx) => !boardPieces.some((b) => b.pieceIndex === idx)),
        [trayOrder, boardPieces],
    );

    const placementHintTrayIndices = useMemo(() => {
        if (!placementHint?.pieceIndices?.length) return [];
        if (placementHint.mode === "step") {
            const step = placementHint.stepIndex;
            if (step == null || step < 0 || step >= placementHint.pieceIndices.length) return [];
            return [placementHint.pieceIndices[step]];
        }
        return placementHint.pieceIndices;
    }, [placementHint]);

    const placementHintBoardIndices = useMemo(() => {
        if (!placementHint?.pieceIndices?.length) return [];
        if (placementHint.mode === "pick") return [];
        if (placementHint.mode === "all") return placementHint.pieceIndices;
        const step = placementHint.stepIndex;
        if (step == null || step < 0 || step >= placementHint.pieceIndices.length) return [];
        return [placementHint.pieceIndices[step]];
    }, [placementHint]);

    const placementHintTraySet = useMemo(
        () => new Set(placementHintTrayIndices),
        [placementHintTrayIndices],
    );

    useEffect(() => {
        if (!placementHintTrayIndices.length) return;
        const el = traySlotRefs.current.get(placementHintTrayIndices[0]);
        el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }, [placementHintTrayIndices]);

    const placedCount = boardPieces.length;
    const isSolved =
        total > 0 &&
        boardPieces.length === total &&
        boardPieces.every((b) => {
            const hx = (b.pieceIndex % grid) * cellPx;
            const hy = Math.floor(b.pieceIndex / grid) * cellPx;
            const tol = cellPx * 0.22;
            return Math.abs(b.x - hx) <= tol && Math.abs(b.y - hy) <= tol;
        });

    useEffect(() => {
        if (successDelayTimerRef.current) {
            window.clearTimeout(successDelayTimerRef.current);
            successDelayTimerRef.current = null;
        }
        successShownRef.current = false;
        playStartMsRef.current = null;
        pauseStartedAtRef.current = null;
        pausedAccumMsRef.current = 0;
        moveCountRef.current = 0;
        setPaused(false);
    }, [storyId, jigsawId]);

    useEffect(() => {
        if (loading || error || !template) return;
        if (playStartMsRef.current == null) {
            playStartMsRef.current = Date.now();
        }
    }, [loading, error, template]);

    useEffect(() => {
        if (paused) {
            if (pauseStartedAtRef.current == null) {
                pauseStartedAtRef.current = Date.now();
            }
            return;
        }
        if (pauseStartedAtRef.current != null) {
            pausedAccumMsRef.current += Date.now() - pauseStartedAtRef.current;
            pauseStartedAtRef.current = null;
        }
    }, [paused]);

    useEffect(() => {
        if (!isSolved || successShownRef.current) return undefined;
        successShownRef.current = true;

        if (pauseStartedAtRef.current != null) {
            pausedAccumMsRef.current += Date.now() - pauseStartedAtRef.current;
            pauseStartedAtRef.current = null;
        }
        setPaused(false);

        const timer = window.setTimeout(() => {
            if (successDelayTimerRef.current === timer) {
                successDelayTimerRef.current = null;
            }
            triggerConfetti(10000);
        }, SOLVE_SUCCESS_DELAY_MS);
        successDelayTimerRef.current = timer;

        return () => {
            window.clearTimeout(timer);
            if (successDelayTimerRef.current === timer) {
                successDelayTimerRef.current = null;
                // Allow re-schedule if effect re-runs before the delay fires.
                successShownRef.current = false;
            }
        };
    }, [isSolved, triggerConfetti]);

    const hasMisplacedOnBoard = useMemo(() => {
        if (!(grid > 0) || cellPx <= 0) return false;
        const tol = cellPx * 0.22;
        return boardPieces.some((b) => {
            const hx = (b.pieceIndex % grid) * cellPx;
            const hy = Math.floor(b.pieceIndex / grid) * cellPx;
            return Math.abs(b.x - hx) > tol || Math.abs(b.y - hy) > tol;
        });
    }, [boardPieces, grid, cellPx]);

    const handleSweepMisplaced = useCallback(() => {
        if (!(grid > 0) || cellPx <= 0) return;
        const tol = cellPx * 0.22;
        setBoardPieces((prev) =>
            prev.filter((b) => {
                const hx = (b.pieceIndex % grid) * cellPx;
                const hy = Math.floor(b.pieceIndex / grid) * cellPx;
                return Math.abs(b.x - hx) <= tol && Math.abs(b.y - hy) <= tol;
            }),
        );
    }, [grid, cellPx]);

    const handlePlacementHintClick = useCallback(async () => {
        if (isSolved || placementHintBusy) return;
        const picked = pickFirstTrayPieces(trayPieces, PLACEMENT_HINT_COUNT);
        if (!picked.length) return;

        const runId = placementHintRunIdRef.current + 1;
        placementHintRunIdRef.current = runId;
        setPlacementHintBusy(true);
        setPlacementHint(null);

        const wait = (ms) =>
            new Promise((resolve) => {
                const timer = window.setTimeout(resolve, ms);
                fxTimersRef.current.push(timer);
            });

        try {
            setPlacementHint({ pieceIndices: picked, mode: "pick", stepIndex: null });
            await wait(PLACEMENT_HINT_PICK_MS);
            for (let i = 0; i < picked.length; i += 1) {
                if (placementHintRunIdRef.current !== runId) return;
                setPlacementHint({ pieceIndices: picked, mode: "step", stepIndex: i });
                await wait(PLACEMENT_HINT_STEP_MS);
            }
            if (placementHintRunIdRef.current !== runId) return;
            setPlacementHint({ pieceIndices: picked, mode: "all", stepIndex: null });
            await wait(PLACEMENT_HINT_HOLD_MS);
            if (placementHintRunIdRef.current !== runId) return;
            setPlacementHint(null);
        } finally {
            if (placementHintRunIdRef.current === runId) {
                setPlacementHintBusy(false);
            }
        }
    }, [isSolved, placementHintBusy, trayPieces]);

    useEffect(() => {
        boardPiecesRef.current = boardPieces;
    }, [boardPieces]);

    useEffect(() => {
        draggingRef.current = dragging;
    }, [dragging]);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
        AudioService.preload(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        return () => {
            AudioService.release(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
            AudioService.release(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        };
    }, []);

    useEffect(
        () => () => {
            placementHintRunIdRef.current += 1;
            for (const timer of fxTimersRef.current) window.clearTimeout(timer);
            fxTimersRef.current = [];
        },
        [],
    );

    const triggerPieceShine = useCallback((boardId, pieceIndex) => {
        if (!boardId || pieceIndex == null) return;
        const id = `${boardId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setActivePieceShines((prev) => [...prev, { boardId, pieceIndex, id }]);
        fxTimersRef.current.push(
            window.setTimeout(() => {
                setActivePieceShines((prev) => prev.filter((entry) => entry.id !== id));
            }, PIECE_SHINE_CLEANUP_MS),
        );
    }, []);

    const triggerPieceSettle = useCallback((boardId) => {
        if (!boardId) return;
        ensurePieceSettleStyle();
        const key = `${boardId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setPieceSettleKeys((prev) => ({ ...prev, [boardId]: key }));
        fxTimersRef.current.push(
            window.setTimeout(() => {
                setPieceSettleKeys((prev) => {
                    if (prev[boardId] !== key) return prev;
                    const next = { ...prev };
                    delete next[boardId];
                    return next;
                });
            }, PIECE_SETTLE_TOTAL_MS + 40),
        );
    }, []);

    const pickLinePraise = useCallback(() => {
        const keys = [
            "puzzle.sliding.praise-row-complete",
            "puzzle.sliding.praise-good-job",
            "puzzle.sliding.praise-great",
            "puzzle.sliding.praise-excellent",
        ];
        const fallbacks = ["Row Complete!", "Good Job!", "Great!", "Excellent!"];
        const i = Math.floor(Math.random() * keys.length);
        const translated = t(keys[i]);
        return translated === keys[i] ? fallbacks[i] : translated;
    }, [t]);

    const triggerLineMatch = useCallback(
        (prev, next, alreadyShinedPieceIndex = null) => {
            const found = findJigsawLineMatchCelebration(prev, next, grid, cellPx);
            if (!found) return false;
            // Shine every piece of the completed row/column, not only the piece just dropped.
            const lineCells = new Set(found.cells);
            for (const piece of next) {
                if (!lineCells.has(piece.pieceIndex)) continue;
                if (piece.pieceIndex === alreadyShinedPieceIndex) continue;
                triggerPieceShine(piece.id, piece.pieceIndex);
            }
            lineMatchFxIdRef.current += 1;
            const id = lineMatchFxIdRef.current;
            setLineMatchFx({ id, rows: found.rows, cols: found.cols, praise: pickLinePraise() });
            AudioService.play(AUDIO_EFFECT.SLIDING_LINE_MATCH);
            fxTimersRef.current.push(
                window.setTimeout(() => {
                    setLineMatchFx((cur) => (cur?.id === id ? null : cur));
                }, LINE_COMPLETE_BANNER_MS + 50),
            );
            return true;
        },
        [grid, cellPx, pickLinePraise, triggerPieceShine],
    );

    /** Matches the Android app: shine when a piece locks home, pop & settle otherwise. */
    const celebratePlacement = useCallback(
        (prev, next, droppedPieceIndex) => {
            const placed = next.find((b) => b.pieceIndex === droppedPieceIndex);
            if (!placed) return false;
            if (didDroppedPieceLandAtHome(prev, next, droppedPieceIndex, grid, cellPx)) {
                AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
                triggerPieceShine(placed.id, droppedPieceIndex);
                return triggerLineMatch(prev, next, droppedPieceIndex);
            }
            if (isPieceExactlyAtHome(placed, grid, cellPx)) return false;
            const was = prev.find((b) => b.pieceIndex === droppedPieceIndex);
            if (was && was.id === placed.id && was.x === placed.x && was.y === placed.y) return false;
            triggerPieceSettle(placed.id);
            return false;
        },
        [grid, cellPx, triggerPieceShine, triggerPieceSettle, triggerLineMatch],
    );

    pausedRef.current = paused;
    const pieceNativePx = cellPx > 0 ? pieceVisualRenderSizePx(cellPx) : 0;

    const applyFlyFrame = useCallback(
        (frame) => {
            const node = flyNodeRef.current;
            const inner = flyInnerRef.current;
            if (!node) return;
            node.style.left = `${frame.left}px`;
            node.style.top = `${frame.top}px`;
            node.style.width = `${frame.width}px`;
            node.style.height = `${frame.height}px`;
            if (inner && pieceNativePx > 0) {
                inner.style.transform = `scale(${frame.width / pieceNativePx})`;
            }
        },
        [pieceNativePx],
    );

    const abortAutoPlacePending = useCallback(() => {
        for (const entry of autoPlacePendingRef.current) {
            if (entry.timerId != null) window.clearTimeout(entry.timerId);
            entry.cancel?.();
            entry.resolve?.();
        }
        autoPlacePendingRef.current = [];
        setFlying(null);
    }, []);

    const celebratePlacementRef = useRef(celebratePlacement);
    celebratePlacementRef.current = celebratePlacement;
    const applyFlyFrameRef = useRef(applyFlyFrame);
    applyFlyFrameRef.current = applyFlyFrame;

    /**
     * Capture auto-solve: after a short intro, fly every tray piece to its home cell one by one.
     * Pauses with the timer; skipped while color-basket practice is open.
     */
    useEffect(() => {
        if (loading || error || !template || !(grid > 0) || !(boardSide > 0) || !paths) {
            return undefined;
        }
        if (colorBasketPlay) {
            abortAutoPlacePending();
            return undefined;
        }

        let cancelled = false;
        const orderSnapshot = [...trayOrder];
        const cell = cellPx;
        const paceRand = mulberry32(hashSeed(`human-pace:${jigsawId || template?.id || "board"}`));

        const wait = (ms) =>
            new Promise((resolve) => {
                const entry = { resolve };
                entry.timerId = window.setTimeout(() => {
                    autoPlacePendingRef.current = autoPlacePendingRef.current.filter((e) => e !== entry);
                    resolve();
                }, ms);
                autoPlacePendingRef.current.push(entry);
            });

        const waitWhilePaused = async () => {
            while (!cancelled && pausedRef.current) {
                await wait(200);
            }
        };

        /** @returns {boolean} true when this place completed a new row/column */
        const placeAtHome = (pieceIndex) => {
            const prev = boardPiecesRef.current;
            if (prev.some((b) => b.pieceIndex === pieceIndex)) return false;
            const next = [
                ...prev,
                {
                    id: newBoardPieceId(),
                    pieceIndex,
                    x: (pieceIndex % grid) * cell,
                    y: Math.floor(pieceIndex / grid) * cell,
                },
            ];
            moveCountRef.current += 1;
            setBoardPieces(next);
            return Boolean(celebratePlacementRef.current(prev, next, pieceIndex));
        };

        /** @returns {Promise<boolean>} resolves with line-complete flag */
        const flyPiece = (pieceIndex, durationMs) =>
            new Promise((resolve) => {
                const slot = traySlotRefs.current.get(pieceIndex);
                const toRect = boardHomeScreenRect(pieceIndex, grid, boardRef.current);
                if (!slot || !toRect) {
                    resolve(placeAtHome(pieceIndex));
                    return;
                }
                slot.scrollIntoView?.({ block: "nearest", inline: "nearest" });
                const rect = slot.getBoundingClientRect();
                const fromRect = {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                };
                const entry = { resolve: () => resolve(false) };
                setFlying({ pieceIndex, frame: fromRect });
                entry.cancel = animatePieceAlongCurve({
                    fromRect,
                    toRect,
                    control: humanFlyControl(fromRect, toRect, paceRand),
                    durationMs,
                    onFrame: (frame) => applyFlyFrameRef.current(frame),
                    onDone: () => {
                        autoPlacePendingRef.current = autoPlacePendingRef.current.filter(
                            (e) => e !== entry,
                        );
                        setFlying(null);
                        resolve(placeAtHome(pieceIndex));
                    },
                });
                autoPlacePendingRef.current.push(entry);
            });

        const run = async () => {
            // Wait a frame so tray slot refs / board rect are laid out.
            await wait(AUTO_PLACE_INTRO_MS);
            if (cancelled) return;
            if (boardPiecesRef.current.length > 0) return;
            const queue = orderSnapshot.filter(
                (idx) => !boardPiecesRef.current.some((b) => b.pieceIndex === idx),
            );
            if (queue.length === 0) return;
            const baseFlyMs = paceFlyMs(
                queue.length,
                AUTO_PLACE_BUDGET_MS,
                AUTO_PLACE_FLY_MIN_MS,
                AUTO_PLACE_FLY_MAX_MS,
                AUTO_PLACE_GAP_MS,
            );
            for (let i = 0; i < queue.length; i += 1) {
                const pieceIndex = queue[i];
                if (cancelled) return;
                await waitWhilePaused();
                if (cancelled) return;
                if (boardPiecesRef.current.some((b) => b.pieceIndex === pieceIndex)) continue;
                const prev = i > 0 ? queue[i - 1] : null;
                const near = prev != null && chebyshev(prev, pieceIndex, grid) <= 2;
                // Chain nearby pieces faster; pause longer when “looking” for the next area.
                const flyMs = Math.round(
                    Math.min(
                        AUTO_PLACE_FLY_MAX_MS,
                        Math.max(
                            AUTO_PLACE_FLY_MIN_MS,
                            baseFlyMs * (near ? 0.72 + paceRand() * 0.32 : 0.95 + paceRand() * 0.5),
                        ),
                    ),
                );
                const gapMs = near
                    ? Math.round(90 + paceRand() * 160)
                    : Math.round(240 + paceRand() * 480);
                const thinkMs = !near && paceRand() < 0.12 ? Math.round(350 + paceRand() * 650) : 0;
                const lineCompleted = await flyPiece(pieceIndex, flyMs);
                if (cancelled) return;
                // Let the row/column banner play out before the next piece flies.
                if (lineCompleted) {
                    await wait(AUTO_PLACE_LINE_HOLD_MS);
                    if (cancelled) return;
                }
                await wait(gapMs + thinkMs);
            }
        };

        run();
        return () => {
            cancelled = true;
            abortAutoPlacePending();
        };
        // Intentionally keyed to puzzle identity + layout readiness, not celebrate callbacks.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stable auto-run per puzzle load
    }, [
        abortAutoPlacePending,
        boardSide,
        cellPx,
        colorBasketPlay,
        error,
        grid,
        jigsawId,
        loading,
        paths,
        storyId,
        template?.id,
        trayOrder,
    ]);

    const handleMouseDown = useCallback(
        (e, pieceIndex, opts = {}) => {
            e.preventDefault();
            e.stopPropagation();
            if (!template || grid <= 0) return;
            const { fromTray, boardId, boardX, boardY } = opts;
            let grabDx = cellPx / 2;
            let grabDy = cellPx / 2;
            if (fromTray) {
                // Tray pieces are drawn centred on their slot at the tray scale.
                const slotRect = e.currentTarget?.getBoundingClientRect?.();
                if (slotRect) {
                    const slotCx = slotRect.left + slotRect.width / 2;
                    const slotCy = slotRect.top + slotRect.height / 2;
                    grabDx = (e.clientX - slotCx) / trayScale + cellPx / 2;
                    grabDy = (e.clientY - slotCy) / trayScale + cellPx / 2;
                }
            } else if (boardRef.current && boardX != null && boardY != null) {
                const rect = boardRef.current.getBoundingClientRect();
                grabDx = (e.clientX - rect.left) / boardScale - boardX;
                grabDy = (e.clientY - rect.top) / boardScale - boardY;
            }
            setDragging({
                pieceIndex,
                x: e.clientX,
                y: e.clientY,
                fromTray: !!fromTray,
                boardId: boardId ?? null,
                grabDx,
                grabDy,
            });
        },
        [template, grid, cellPx, boardScale, trayScale],
    );

    useEffect(() => {
        if (!dragging || !template || grid <= 0) return undefined;
        const tabMargin = cellPx * 0.35;
        const snapTol = cellPx * 0.38;

        const onMove = (e) => {
            const point = e.touches?.[0] || e;
            if (point?.clientX == null) return;
            if (e.cancelable && e.touches) e.preventDefault();
            setDragging((prev) =>
                prev ? { ...prev, x: point.clientX, y: point.clientY } : null,
            );
        };

        const onUp = (e) => {
            const point = e.changedTouches?.[0] || e;
            const cx = point?.clientX;
            const cy = point?.clientY;
            if (cx == null || cy == null) {
                setDragging(null);
                return;
            }
            const { pieceIndex, fromTray, boardId, grabDx, grabDy } = dragging;
            const boardEl = boardRef.current;
            const trayEl = trayRef.current;

            const dropOnBoard = () => {
                if (!boardEl) return false;
                const rect = boardEl.getBoundingClientRect();
                if (
                    cx < rect.left ||
                    cx > rect.right ||
                    cy < rect.top ||
                    cy > rect.bottom
                ) {
                    return false;
                }
                let nx = (cx - rect.left) / boardScale - grabDx;
                let ny = (cy - rect.top) / boardScale - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, ny));
                const homeX = (pieceIndex % grid) * cellPx;
                const homeY = Math.floor(pieceIndex / grid) * cellPx;
                if (Math.abs(nx - homeX) <= snapTol && Math.abs(ny - homeY) <= snapTol) {
                    nx = homeX;
                    ny = homeY;
                }
                if (fromTray) {
                    const prev = boardPiecesRef.current;
                    const next = [...prev, { id: newBoardPieceId(), pieceIndex, x: nx, y: ny }];
                    moveCountRef.current += 1;
                    setBoardPieces(next);
                    celebratePlacement(prev, next, pieceIndex);
                } else if (boardId) {
                    const prev = boardPiecesRef.current;
                    const next = prev.map((b) =>
                        b.id === boardId ? { ...b, x: nx, y: ny } : b,
                    );
                    moveCountRef.current += 1;
                    setBoardPieces(next);
                    celebratePlacement(prev, next, pieceIndex);
                }
                return true;
            };

            if (dropOnBoard()) {
                setDragging(null);
                return;
            }

            if (!fromTray && boardId && trayEl) {
                const trayRect = trayEl.getBoundingClientRect();
                if (
                    cx >= trayRect.left &&
                    cx <= trayRect.right &&
                    cy >= trayRect.top &&
                    cy <= trayRect.bottom
                ) {
                    setBoardPieces((prev) => prev.filter((b) => b.id !== boardId));
                }
            }

            setDragging(null);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging, template, grid, cellPx, boardScale, celebratePlacement]);

    const handleBack = () => {
        navigate(MAIN_PATH);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    width: FRAME_W,
                    height: FRAME_H,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: SITE_BG,
                }}
            >
                <CircularProgress size={40} sx={{ color: TEXT.primary }} />
            </Box>
        );
    }

    if (error || !template || !paths || grid <= 0) {
        return (
            <Box sx={{ width: FRAME_W, height: FRAME_H, p: 4, bgcolor: SITE_BG }}>
                <Typography sx={{ color: TEXT.primary }}>{error || "Puzzle not available"}</Typography>
                <IconButton onClick={handleBack} sx={{ mt: 2, color: TEXT.primary }}>
                    <ArrowBackIcon />
                </IconButton>
            </Box>
        );
    }

    if (colorBasketPlay?.basket && imageUrl && paths) {
        return (
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: FRAME_W,
                    height: FRAME_H,
                    maxWidth: FRAME_W,
                    maxHeight: FRAME_H,
                    overflow: "hidden",
                    boxSizing: "border-box",
                    p: `${PAD}px`,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: SITE_BG,
                }}
            >
                <SlidingPlayMistBackground variant="night" />
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <AdminColorBasketPlayPanel
                        basket={colorBasketPlay.basket}
                        allBaskets={colorBasketPlay.baskets}
                        basketCount={colorBasketPlay.basketCount}
                        jigsawId={template.id}
                        grid={grid}
                        imageUrl={imageUrl}
                        paths={paths}
                        svgPathFallback={svgPathFallback}
                        hideMainBoard
                        hideCloseButton
                        simpleMode
                        promoCapture
                        dialogSx={captureDialogSx}
                        onBasketHomePieces={handleBasketHomePieces}
                        onBackToMainPlay={handleBackFromBasket}
                        puzzleTitle={puzzleTitle}
                        toolbarLeading={
                            <Tooltip title={t("puzzle.jigsaw.main-play", "Main Play")} arrow>
                                <IconButton
                                    onClick={handleBackFromBasket}
                                    sx={{ ...PLAY_TOOLBAR_ICON_SX }}
                                    aria-label={t("puzzle.jigsaw.main-play", "Main Play")}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    />
                </Box>
            </Box>
        );
    }

    const toolbarActions = (
        <>
            {jigsawShareUrl ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        "& .MuiIconButton-root": {
                            ...PLAY_TOOLBAR_ICON_SX,
                            color: `${TEXT.primary} !important`,
                            backgroundColor: "transparent !important",
                            border: "none !important",
                            boxShadow: "none !important",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.08) !important" },
                        },
                        "& .MuiSvgIcon-root": { fontSize: "2.35rem !important" },
                    }}
                >
                    <SocialShareDropdown
                        puzzleTitle={puzzleTitle}
                        puzzleId={jigsawShareId}
                        puzzleType="JIGSAW"
                        shareUrl={jigsawShareUrl}
                        tooltipTitle="Share"
                    />
                </Box>
            ) : null}
            <Box
                sx={{
                    display: "inline-flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                    "& .MuiTypography-root": {
                        fontSize: "2rem",
                        fontWeight: 800,
                        letterSpacing: "0.02em",
                        color: TEXT.primary,
                    },
                    minWidth: "6rem",
                }}
            >
                <PlayElapsedTimer
                    active={!loading && !error}
                    running={!isSolved && !paused}
                    ariaLabel="Elapsed time"
                />
                {!isSolved ? (
                    <IconButton
                        onClick={() => setPaused((p) => !p)}
                        sx={{
                            ...PLAY_TOOLBAR_ICON_SX,
                            ml: -0.5,
                            flexShrink: 0,
                        }}
                        aria-label={paused ? "Resume timer" : "Pause timer"}
                        aria-pressed={paused}
                    >
                        {paused ? <PlayArrowIcon /> : <PauseIcon />}
                    </IconButton>
                ) : null}
            </Box>
            <Tooltip title="Remove pieces that are not in the correct place" arrow>
                <span>
                    <IconButton
                        data-play-toolbar-no-drag
                        onClick={handleSweepMisplaced}
                        disabled={!hasMisplacedOnBoard}
                        sx={{
                            ...PLAY_TOOLBAR_ICON_SX,
                            flexShrink: 0,
                            "&.Mui-disabled": {
                                color: TEXT.primary,
                                opacity: 0.42,
                            },
                        }}
                        aria-label="Remove pieces that are not in the correct place"
                    >
                        <CleaningServicesIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Hint: show where tray pieces belong" arrow>
                <span>
                    <IconButton
                        data-play-toolbar-no-drag
                        disabled={isSolved || placementHintBusy || trayPieces.length === 0}
                        onClick={handlePlacementHintClick}
                        sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                        aria-label="Hint"
                    >
                        <LightbulbOutlinedIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Box
                sx={{
                    display: "inline-flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                }}
            >
                <Tooltip title="Zoom out" arrow>
                    <span>
                        <IconButton
                            data-play-toolbar-no-drag
                            disabled={!canZoomOut}
                            onClick={() => handleBoardZoomStep(-1)}
                            sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                            aria-label="Zoom out"
                        >
                            <ZoomOutIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {boardZoom !== 1 ? (
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: TEXT.secondary,
                            minWidth: "3rem",
                            textAlign: "center",
                        }}
                    >
                        {`${Math.round(boardZoom * 100)}%`}
                    </Typography>
                ) : null}
                <Tooltip title="Zoom in" arrow>
                    <span>
                        <IconButton
                            data-play-toolbar-no-drag
                            disabled={!canZoomIn}
                            onClick={() => handleBoardZoomStep(1)}
                            sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                            aria-label="Zoom in"
                        >
                            <ZoomInIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {imageUrl ? (
                <Tooltip title="View original image" arrow>
                    <IconButton
                        data-play-toolbar-no-drag
                        onClick={() => setImageDialogOpen(true)}
                        sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                        aria-label="View original image"
                    >
                        <OpenInNewIcon />
                    </IconButton>
                </Tooltip>
            ) : null}
            {colorBasketsAvailable && !isSolved ? (
                <Tooltip title={t("puzzle.jigsaw.color-baskets", "Color baskets")} arrow>
                    <IconButton
                        data-play-toolbar-no-drag
                        onClick={() => setColorBasketOverlayOpen(true)}
                        sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                        aria-label={t("puzzle.jigsaw.color-baskets", "Color baskets")}
                    >
                        <FaShoppingBasket size={36} />
                    </IconButton>
                </Tooltip>
            ) : null}
            <Tooltip title="Settings" arrow>
                <IconButton
                    data-play-toolbar-no-drag
                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                    sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                    aria-label="Settings"
                    aria-haspopup="true"
                    aria-expanded={Boolean(settingsAnchor)}
                >
                    <SettingsIcon />
                </IconButton>
            </Tooltip>
        </>
    );

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: FRAME_W,
                height: FRAME_H,
                maxWidth: FRAME_W,
                maxHeight: FRAME_H,
                overflow: "hidden",
                boxSizing: "border-box",
                p: `${PAD}px`,
                display: "flex",
                flexDirection: "column",
                bgcolor: SITE_BG,
                color: TEXT.primary,
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    // Match play-jigsaw: toolbar on top; landscape adds preview left + tray right.
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        height: TOOLBAR_H,
                        mb: `${TOOLBAR_GAP}px`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            flex: "1 1 auto",
                            pr: 1,
                        }}
                    >
                        <IconButton
                            onClick={handleBack}
                            sx={{ ...PLAY_TOOLBAR_ICON_SX, flexShrink: 0 }}
                            aria-label="Back"
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            component="h1"
                            noWrap
                            title={puzzleTitle}
                            sx={{
                                fontWeight: 800,
                                color: TEXT.primary,
                                minWidth: 0,
                                lineHeight: 1.2,
                                fontSize: "1.85rem",
                            }}
                        >
                            {puzzleTitle}
                        </Typography>
                    </Box>
                    <PlayToolbarScrollRow
                        sx={{
                            mb: 0,
                            mt: 0,
                            minWidth: 0,
                            width: "auto",
                            flex: "0 1 auto",
                            ml: "auto",
                            ...panelSurfaceSx,
                            borderRadius: 3,
                            px: 1.5,
                            py: 0.75,
                            "& > div > div": { gap: 1.5 },
                            "& .MuiTypography-root": { color: TEXT.primary },
                        }}
                    >
                        {toolbarActions}
                    </PlayToolbarScrollRow>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: trayBeside ? "row" : "column",
                            alignItems: trayBeside ? "center" : "center",
                            justifyContent: "center",
                            gap: trayBeside ? `${PREVIEW_GAP}px` : `${TRAY_GAP}px`,
                            flexShrink: 0,
                            maxWidth: "100%",
                            maxHeight: "100%",
                        }}
                    >
                        {trayBeside && previewSide > 0 && imageUrl && !isSolved ? (
                            <Box
                                sx={{
                                    width: previewSide,
                                    height: previewSide,
                                    flexShrink: 0,
                                    position: "relative",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    boxShadow: 2,
                                    bgcolor: SURFACE.sunken,
                                    border: `1px solid ${SURFACE.border}`,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setImageDialogOpen(true)}
                                    aria-label="View original image"
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        left: 4,
                                        bgcolor: "rgba(0,0,0,0.45)",
                                        color: "#fff",
                                        p: 0.35,
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                                    }}
                                >
                                    <OpenInNewIcon sx={{ fontSize: "1.1rem" }} />
                                </IconButton>
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: trayBeside ? "row" : "column",
                                alignItems: trayBeside ? "flex-start" : "center",
                                justifyContent: "center",
                                gap: `${TRAY_GAP}px`,
                                flexShrink: 0,
                            }}
                        >
                        <Box
                            ref={boardViewportRef}
                            onPointerDown={handleBoardPanPointerDown}
                            onPointerMove={handleBoardPanPointerMove}
                            onPointerUp={handleBoardPanPointerUp}
                            onPointerCancel={handleBoardPanPointerUp}
                            sx={{
                                width: boardSide,
                                height: boardSide,
                                flexShrink: 0,
                                position: "relative",
                                // Zoomed-in boards scroll/pan inside the slot; zoomed-out ones stay centered.
                                overflow: boardZoom > 1 ? "auto" : "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                touchAction: boardZoom > 1 ? "none" : "auto",
                                cursor: boardZoom > 1 ? "grab" : "default",
                                scrollbarWidth: "none",
                                "&::-webkit-scrollbar": { display: "none" },
                                "& > *": { flexShrink: 0, margin: "auto" },
                            }}
                        >
                            {isSolved && imageUrl ? (
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{
                                        width: boardDisplaySide,
                                        height: boardDisplaySide,
                                        objectFit: "cover",
                                        borderRadius: 1,
                                        display: "block",
                                    }}
                                />
                            ) : (
                                <CanvasBoard
                                    boardRef={boardRef}
                                    boardPieces={boardPieces}
                                    grid={grid}
                                    imageUrl={imageUrl}
                                    paths={paths}
                                    svgPathFallback={svgPathFallback}
                                    dragging={dragging}
                                    activePieceShines={activePieceShines}
                                    pieceSettleKeys={pieceSettleKeys}
                                    lineMatchFx={lineMatchFx}
                                    onLineMatchFxComplete={() => setLineMatchFx(null)}
                                    placementHintPieceIndices={placementHintBoardIndices}
                                    showDragOutline={false}
                                    placedCount={placedCount}
                                    total={total}
                                    isSolved={isSolved}
                                    showPieceGridCoords={false}
                                    showHeader={false}
                                    fitParent={false}
                                    matPreset={boardMat}
                                    matColor={boardMat.hex}
                                    grooveStyle={boardGrooveStyle}
                                    explicitDisplaySidePx={boardDisplaySide}
                                    onBoardPieceMouseDown={(e, pieceIndex, meta) =>
                                        handleMouseDown(e, pieceIndex, {
                                            boardId: meta?.boardId,
                                            boardX: meta?.boardX,
                                            boardY: meta?.boardY,
                                        })
                                    }
                                />
                            )}
                        </Box>

                        {/* Hide the tray plate once every piece is on the board (no empty strip). */}
                        {trayPieces.length > 0 ? (
                            <Box
                                sx={{
                                    width: trayBeside ? trayThicknessPx : boardSide,
                                    height: trayBeside ? boardSide : trayThicknessPx,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    borderRadius: 2,
                                    bgcolor: TRAY_PLATE_BG,
                                    border: `1px solid ${SURFACE.border}`,
                                    boxSizing: "border-box",
                                }}
                            >
                                <TrayScroller
                                    containerRef={trayRef}
                                    {...(trayBeside
                                        ? {
                                              columnCount: TRAY_COLUMN_COUNT,
                                              maxHeightPx: boardSide,
                                          }
                                        : {
                                              rowCount: TRAY_ROW_COUNT,
                                              maxWidthPx: boardSide,
                                          })}
                                    cellSizePx={trayCirclePx}
                                    gapPx={TRAY_CELL_GAP}
                                >
                                    {trayPieces.map((pieceIndex) => {
                                        const isDraggingThis =
                                            dragging?.pieceIndex === pieceIndex &&
                                            dragging?.fromTray;
                                        const isFlyingThis = flying?.pieceIndex === pieceIndex;
                                        const isHinted = placementHintTraySet.has(pieceIndex);
                                        return (
                                            <Box
                                                key={pieceIndex}
                                                ref={(el) => {
                                                    if (el) traySlotRefs.current.set(pieceIndex, el);
                                                    else traySlotRefs.current.delete(pieceIndex);
                                                }}
                                                onMouseDown={(e) =>
                                                    handleMouseDown(e, pieceIndex, {
                                                        fromTray: true,
                                                    })
                                                }
                                                sx={{
                                                    width: trayCirclePx,
                                                    height: trayCirclePx,
                                                    flexShrink: 0,
                                                    borderRadius: "50%",
                                                    overflow: "hidden",
                                                    cursor: "grab",
                                                    opacity: isDraggingThis || isFlyingThis ? 0 : 1,
                                                    position: "relative",
                                                    bgcolor: "rgba(255,255,255,0.08)",
                                                    border: `1px solid ${SURFACE.border}`,
                                                    boxSizing: "border-box",
                                                    ...(isHinted
                                                        ? {
                                                              animation: `${trayHintPulse} 1.1s ease-in-out infinite`,
                                                              zIndex: 2,
                                                          }
                                                        : null),
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        top: "50%",
                                                        width: 0,
                                                        height: 0,
                                                        overflow: "visible",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                            transform: `translate(-50%, -50%) scale(${trayScale})`,
                                                            transformOrigin: "center center",
                                                            lineHeight: 0,
                                                        }}
                                                    >
                                                        <PieceVisual
                                                            pieceIndex={pieceIndex}
                                                            imageUrl={imageUrl}
                                                            grid={grid}
                                                            paths={paths}
                                                            showGridCoords={false}
                                                            anchorToCell={false}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </TrayScroller>
                            </Box>
                        ) : null}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {dragging && paths ? (
                <Box
                    sx={{
                        position: "fixed",
                        left:
                            dragging.x -
                            (dragging.grabDx + pieceVisualCellOffsetPx(cellPx)) *
                                (dragging.fromTray ? trayScale : boardScale),
                        top:
                            dragging.y -
                            (dragging.grabDy + pieceVisualCellOffsetPx(cellPx)) *
                                (dragging.fromTray ? trayScale : boardScale),
                        pointerEvents: "none",
                        zIndex: 9999,
                        transform: `scale(${dragging.fromTray ? trayScale : boardScale})`,
                        transformOrigin: "top left",
                        lineHeight: 0,
                    }}
                >
                    <PieceVisual
                        pieceIndex={dragging.pieceIndex}
                        imageUrl={imageUrl}
                        grid={grid}
                        paths={paths}
                        showGridCoords={false}
                        anchorToCell={false}
                    />
                </Box>
            ) : null}

            {flying && paths ? (
                <Box
                    ref={flyNodeRef}
                    sx={{
                        position: "fixed",
                        left: flying.frame.left,
                        top: flying.frame.top,
                        width: flying.frame.width,
                        height: flying.frame.height,
                        pointerEvents: "none",
                        zIndex: 9999,
                    }}
                >
                    <Box
                        ref={flyInnerRef}
                        sx={{
                            transform: `scale(${
                                pieceNativePx > 0 ? flying.frame.width / pieceNativePx : 1
                            })`,
                            transformOrigin: "top left",
                            lineHeight: 0,
                        }}
                    >
                        <PieceVisual
                            pieceIndex={flying.pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            showGridCoords={false}
                            anchorToCell={false}
                        />
                    </Box>
                </Box>
            ) : null}

            <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                disableScrollLock
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            p: 1.5,
                            minWidth: 200,
                            borderRadius: 2,
                            boxShadow: 4,
                        },
                    },
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                >
                    Board mat color
                </Typography>
                <BoardMatColorSwitcher
                    variant="panel"
                    matId={boardMatId}
                    onMatIdChange={setBoardMatId}
                    ariaLabel="Board mat color"
                />
            </Popover>

            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="md"
                fullWidth
                sx={captureDialogSx}
            >
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        sx={{
                            display: "block",
                            width: "100%",
                            height: "auto",
                            maxHeight: "80vh",
                            objectFit: "contain",
                            bgcolor: "#0f172a",
                        }}
                    />
                ) : null}
            </Dialog>

            <JigsawColorBasketOverlay
                open={colorBasketOverlayOpen}
                onClose={handleColorBasketOverlayClose}
                storyId={storyId}
                jigsawId={template?.id}
                grid={grid}
                imageUrl={imageUrl}
                puzzleTitle={puzzleTitle}
                onBasketChosen={handleBasketChosen}
                dialogSx={captureDialogSx}
                paperSx={{ maxHeight: Math.round(FRAME_H * 0.9) }}
            />
        </Box>
    );
}
